import { observer } from "mobx-react-lite";
import { Form as FinalForm, Field } from "react-final-form";
import { isRequired, combineValidators } from "revalidate";
import React, { useContext, useEffect, useState } from "react";

// reactstrap components
import { Alert, Button, FormGroup, Form, Col, Row, Label } from "reactstrap";
import { FORM_ERROR } from "final-form";
import TextInput from "../common/TextInput";
import { RootStoreContext } from "../../app/stores/rootStore";
import { toast } from "react-toastify";
import LogoCompany from "../common/LogoCompany";
import { useTranslation } from "react-i18next";
import { translationHookOptions } from "../../app/localization/localization";
import { IAuthenticatorFormValues } from "../../app/models/user";
import QRCodeComponent from "../common/QRCodeComponent";
import ErrorsList from "../common/ErrorsList";
import { ResetPwdForm } from "./ResetPwdForm";

const validateStep1 = combineValidators({
  email: isRequired("email"),
  password: isRequired("password"),
});

const validateStep2 = combineValidators({
  email: isRequired("email"),
  password: isRequired("password"),
  otp: isRequired("otp"),
});

export const LoginAuthenticatorAppForm = () => {
  const rootStore = useContext(RootStoreContext);
  const {
    redirectToLogin,
    loginAuthenticatorAppStep1,
    loginAuthenticatorStep2,
    loginAuthenticatorApp,
    getAuthenticatorQrcodeInfo,
    authenticatorQRcodeInfo,
    clearAuthenticatorInfo,
    authenticatorAppResetToken,
    reset2FA_APP,
  } = rootStore.userStore;
  const { company, correctPassword } = rootStore.commonStore;
  const { showRegisterForm, showResetPwdForm } = rootStore.authStore;
  const [credentialsOk, setCredentialsOk] = useState(false);
  const [t] = useTranslation(
    ["translation", "guest", "authenticatorapp", "errors"],
    translationHookOptions
  );
  const [sendReset2AF, setSendReset2AF] = useState(false);
  const [formvalues, setFormValues] = useState<IAuthenticatorFormValues>();
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const toAndroidIntent = (
    otpauthUri: string,
    pkg?: "com.google.android.apps.authenticator2" | "com.azure.authenticator"
  ) => {
    const payload = otpauthUri.replace(/^otpauth:\/\//, "");
    const pkgPart = pkg ? `;package=${pkg}` : "";
    return `intent://${payload}#Intent;scheme=otpauth${pkgPart};end`;
  };

  const openAuthenticator = (otpauthUri: string) => {
    // 1) tenta apertura schema custom
    try {
      window.location.assign(otpauthUri);
    } catch {}
    // 2) dopo un attimo, fallback
    setTimeout(() => {
      if (isAndroid) {
        try {
          window.location.assign(toAndroidIntent(otpauthUri));
        } catch {}
        setTimeout(() => {
          try {
            window.location.assign(
              toAndroidIntent(
                otpauthUri,
                "com.google.android.apps.authenticator2"
              )
            );
          } catch {}
        }, 400);
      } else if (isIOS) {
        // su iOS non esistono intent: mostra istruzioni
        toast.warn(
          t("info_button_link_authenticator_IOS", { ns: "authenticatorapp" })
        );
      } else {
        toast.warn(
          t("info_button_link_authenticator", { ns: "authenticatorapp" })
        );
      }
    }, 600);
  };
  // copia con fallback anche su http/in-app browser
  const copyToClipboard = async (
    text: string,
    onOk?: () => void,
    onErr?: () => void
  ) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      onOk && onOk();
    } catch {
      onErr && onErr();
    }
  };

  const linkToRegister = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    showRegisterForm();
  };

  const linkToResetPwd = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    showResetPwdForm();
  };

  const linkToBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    redirectToLogin();
  };

  const LinkReset2FA = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    if (formvalues) {
      reset2FA_APP(formvalues).then(() => {
        setSendReset2AF(true);
      });
    }
  };
  const handleMicrophoneEditRequest = (e: any) => {
    e.preventDefault();

    const userPassword = window.prompt(t("insert_password"));
    if (userPassword === correctPassword) {
      window.location.href = `${window.location.origin}/player//microphone`;
    } else {
      alert(t("incorrect_password"));
    }
  };
  useEffect(() => {
    return () => {
      clearAuthenticatorInfo();
    };
  }, [clearAuthenticatorInfo]);

  return (
    <div className="form-wrapper  mt-0">
      <div>
        <LogoCompany className="loginLogo" />
      </div>
      <FinalForm
        initialValues={{
          email: "",
          password: "",
          companyId: company ? company.id : 1,
          token: "",
        }}
        onSubmit={(values: any) =>
          credentialsOk
            ? loginAuthenticatorStep2(values).catch((error) => ({
                [FORM_ERROR]: error,
              }))
            : loginAuthenticatorAppStep1(values)
                .then((res) => {
                  if (res) {
                    if (res && res.isVisibleQrCode) {
                      getAuthenticatorQrcodeInfo(res.token, company?.id || 0);
                    }
                    setCredentialsOk(true);
                    setFormValues(values);
                  }
                })
                .catch((error) => ({
                  [FORM_ERROR]: error,
                }))
        }
        validate={credentialsOk ? validateStep2 : validateStep1}
        render={({
          handleSubmit,
          submitting,
          submitError,
          invalid,
          pristine,
          dirtySinceLastSubmit,
        }) => (
          <Form onSubmit={handleSubmit} role="form">
            {!credentialsOk && (
              <>
                {authenticatorAppResetToken ? (
                  <>
                    <div
                      className="text-center mb-4"
                      style={{ marginTop: "3px" }}
                    >
                      <b>
                        {t("info_reset_2FA_AuthenticatorAPP", {
                          ns: "authenticatorapp",
                        })}{" "}
                      </b>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="text-center mb-4"
                      style={{ marginTop: "3px" }}
                    >
                      <b>
                        {loginAuthenticatorApp?.isVisibleQrCode
                          ? t("title_configuration_authenticatorAPP", {
                              ns: "authenticatorapp",
                            })
                          : t("title_authenticatorApp", {
                              ns: "authenticatorapp",
                            })}
                      </b>
                    </div>
                  </>
                )}

                <FormGroup className="form-group input-group-lg">
                  <Field
                    className="form-control"
                    name="email"
                    component={TextInput}
                    placeholder={t("email_placeholder")}
                    autoComplete="email"
                  />
                </FormGroup>

                {credentialsOk == false && (
                  <>
                    <FormGroup className="form-group input-group-lg">
                      <Field
                        className="form-control"
                        name="password"
                        component={TextInput}
                        placeholder={t("password_placeholder")}
                        type="password"
                        autoComplete="current-password"
                      />
                      <Field
                        name="companyId"
                        component={TextInput}
                        type="hidden"
                      />
                    </FormGroup>
                  </>
                )}
                <FormGroup className="form-group input-group-lg">
                  <Field name="companyId" component={TextInput} type="hidden" />
                </FormGroup>
              </>
            )}
            {credentialsOk && (
              <>
                <div>
                  {loginAuthenticatorApp &&
                    loginAuthenticatorApp.isVisibleQrCode &&
                    authenticatorQRcodeInfo && (
                      <>
                        <Row style={{ marginTop: "3px" }}>
                          <Col className="text-center">
                            <b>
                              {" "}
                              {t("info_QrCode", {
                                ns: "authenticatorapp",
                              })}{" "}
                            </b>
                          </Col>
                        </Row>
                        <div
                          className="text-center mb-4"
                          style={{ marginTop: "2%" }}
                        >
                          <QRCodeComponent data={authenticatorQRcodeInfo.url} />
                        </div>
                        <Row>
                          <Col className="text-center">
                            <small>
                              {" "}
                              {t("info_QrCode_authenticatorAPP", {
                                ns: "authenticatorapp",
                              })}{" "}
                            </small>
                          </Col>
                        </Row>
                        <Row style={{ marginTop: "2%" }}>
                          <Col className="text-center text-break">
                            <b style={{ fontSize: "85%" }}>
                              {authenticatorQRcodeInfo.secret}
                            </b>
                          </Col>
                        </Row>
                        <Row>
                          <Col className="text-center">
                            <Button
                              style={{ fontWeight: "bold" }}
                              className="buttonSimple btn btn-secondary fw-bold"
                              onClick={() =>
                                copyToClipboard(
                                  authenticatorQRcodeInfo.secret,
                                  () =>
                                    toast.success(
                                      t("copy_success", {
                                        ns: "authenticatorapp",
                                      })
                                    ),
                                  () => toast.error("error")
                                )
                              }
                            >
                              <i className="far fa-copy" />{" "}
                              {t("copy_to_clipboard", {
                                ns: "authenticatorapp",
                              })}
                            </Button>
                          </Col>
                        </Row>
                        <Row style={{ marginTop: "2%" }}>
                          <Col className="text-center">
                            <small>
                              {" "}
                              {t("info_phone_authenticatorAPP", {
                                ns: "authenticatorapp",
                              })}{" "}
                            </small>
                          </Col>
                        </Row>
                        {/* LINK DIRETTO otpauth:// */}
                        {/* <Row className="mb-2">
                          <Col className="text-center">
                            <Button
                            style={{ fontWeight: "bold" }}
                              className="buttonSimple btn btn-secondary fw-bold"
                              tag="a"
                              href={authenticatorQRcodeInfo.url}
                              color="primary"
                            >
                              {t("open_authenticatorApp", {
                                ns: "authenticatorapp",
                              })}
                            </Button>
                          </Col>
                        </Row>{" "} */}
                        <Row className="mb-2">
                          <Col className="text-center">
                            <Button
                              onClick={() =>
                                openAuthenticator(authenticatorQRcodeInfo.url)
                              }
                              style={{ fontWeight: "bold" }}
                              className="buttonSimple btn btn-secondary fw-bold"
                              href={authenticatorQRcodeInfo.url}
                              color="primary"
                            >
                              {t("open_authenticatorApp", {
                                ns: "authenticatorapp",
                              })}
                            </Button>
                          </Col>
                        </Row>{" "}
                      </>
                    )}
                </div>
                {loginAuthenticatorApp &&
                loginAuthenticatorApp.isVisibleQrCode ? (
                  <>
                    <Row style={{ marginTop: "2%", marginBottom: "6%" }}>
                      <Col className="text-center">
                        <small>
                          {" "}
                          {t("info_OTP_authenticatorAPP", {
                            ns: "authenticatorapp",
                          })}{" "}
                        </small>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <>
                    <div
                      className="text-center  mb-4"
                      style={{ marginTop: "13%" }}
                    >
                      <b>
                        {t("insert_OTP_authenticatorAPP", {
                          ns: "authenticatorapp",
                        })}
                      </b>
                    </div>
                  </>
                )}

                <FormGroup className="form-group input-group-lg">
                  <Field
                    name="otp"
                    component={TextInput}
                    placeholder={t("otp_placeholder")}
                    autoComplete="off"
                    type="number"
                    className="form-control"
                  />
                </FormGroup>
              </>
            )}

            {!authenticatorAppResetToken &&
              credentialsOk == false &&
              submitError &&
              !dirtySinceLastSubmit && (
                <Row>
                  {submitError && !dirtySinceLastSubmit && (
                    <>
                      {submitError.status === 400 ? (
                        <>
                          <Alert
                            color="danger"
                            className="mx-auto  text-center"
                          >
                            {t("invalid_credentials")}
                          </Alert>
                        </>
                      ) : (
                        <>
                          <ErrorsList submitError={submitError}></ErrorsList>
                        </>
                      )}
                    </>
                  )}
                </Row>
              )}

            {authenticatorAppResetToken &&
              credentialsOk == false &&
              submitError &&
              !dirtySinceLastSubmit && (
                <Row>
                  {submitError && !dirtySinceLastSubmit && (
                    <>
                      <ErrorsList submitError={submitError}></ErrorsList>
                    </>
                  )}
                </Row>
              )}

            {credentialsOk == true && submitError && !dirtySinceLastSubmit && (
              <Alert color="danger">
                {t("authenticatorApp_Otp_invalid", { ns: "errors" })}
              </Alert>
            )}
            {sendReset2AF && (
              <>
                <Alert color="success">
                  {t("send_email_resetAuthenticatorApp", { ns: "toast" })}
                </Alert>
              </>
            )}

            <div className="text-center">
              <Button
                disabled={(invalid && !dirtySinceLastSubmit) || pristine}
                className="btn loginButton btn-block btn-lg"
                color="dark"
                type="submit"
              >
                {/* {t("login")} */}
                {authenticatorAppResetToken ? t("confirm") : t("login")}
              </Button>
              {authenticatorAppResetToken && (
                <>
                  <Row style={{ marginTop: "4%" }}>
                    <Col>
                      <b className=" text-underline">
                        {t("warning_reset_2FA_AuthenticatorAPP", {
                          ns: "authenticatorapp",
                        })}
                      </b>
                    </Col>
                  </Row>
                </>
              )}
            </div>
          </Form>
        )}
      />

      {!authenticatorAppResetToken && credentialsOk == false ? (
        <>
          <Row className="mt-3">
            <Col style={{ paddingRight: "0px" }}>
              <p className="text-muted">{t("forgot_password")}</p>
              <a
                className="btn btn-dark loginButton text-white btn-outline-light btn-sm"
                href="/#"
                onClick={(e) => linkToResetPwd(e)}
              >
                {t("click_here")}
              </a>
            </Col>

            {company && company.allowRegistration && (
              <Col>
                <p className="text-muted">{t("registration_text")}</p>
                <a
                  className="btn btn-dark loginButton text-white btn-outline-light btn-sm"
                  href="/#"
                  onClick={(e) => linkToRegister(e)}
                >
                  {t("registration_link")}
                </a>
              </Col>
            )}
          </Row>

          {company && company.allowGuest && (
            <Row className="mt-3">
              <Col>
                <a className="text-muted text-underline" href="/guest">
                  <u>{t("guest_login_link", { ns: "guest" })}</u>
                </a>
              </Col>
            </Row>
          )}
          {company && company.allowBroadcast && (
            <Col className="text-center" xs="12" style={{ marginTop: "10px" }}>
              <a
                className="text-center text-underline text-muted"
                href={`${window.location.pathname}//microphone`}
                onClick={handleMicrophoneEditRequest}
              >
                <u>{t("microphone_link")}</u>
              </a>
            </Col>
          )}
        </>
      ) : (
        <>
          {loginAuthenticatorApp && !loginAuthenticatorApp.isVisibleQrCode && (
            <>
              <Row className="mt-3">
                <Col style={{ paddingRight: "0px" }}>
                  <p className="text-muted">
                    {t("reset_2FA_AuthenticatorAPP", {
                      ns: "authenticatorapp",
                    })}
                  </p>
                  <a
                    className="btn btn-dark loginButton text-white btn-outline-light btn-sm"
                    href="/#"
                    onClick={(e) => LinkReset2FA(e)}
                  >
                    {t("click_here")}
                  </a>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
      {credentialsOk == true && (
        <>
          <Row style={{ marginTop: "7%" }}>
            <Col style={{ paddingRight: "0px" }}>
              <a
                className="btn btn-dark loginButton text-white btn-outline-light btn-sm"
                href="/#"
                onClick={(e) => linkToBack(e)}
              >
                {t("back")}
              </a>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default observer(LoginAuthenticatorAppForm);
